import { useEffect } from "react";

export default function JobDetailDrawer({
  job,
  operation,
  selectedRoutingStep,
  routing = [],
  routingLoading = false,
  routingError = null,
  activeSessions = {},
  onRoutingStepSelect,
  onClose,
  onStartJob,
  onPauseJob,
  onResumeJob,
  onCompleteJob,
}) {
  const isOpen = !!job;

  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    if (isOpen) window.addEventListener("keydown", handleKey);

    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen || !job) return null;

  // Active session for this job (if any)
  const jobSession = activeSessions[job.id];

  const state = jobSession?.state;
  const isRunning = state === "running";
  const isPaused = state === "paused";
  const hasSession = isRunning || isPaused;

  // Prefer operationName, then operation, then code for display
  const operationLabel =
    operation?.operationName ||
    operation?.operation ||
    operation?.operationCode ||
    null;

  // Only allow START if we have a valid operationCode
  const canStart = !!operation?.operationCode;

  return (
    <aside
      className="
        w-[400px] h-full
        bg-white border-l border-gray-200
        shadow-xl flex flex-col
        z-[60]
      "
    >
      {/* HEADER */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500">
            Job Details
          </div>
          <div className="text-sm font-semibold text-gray-900">
            {job.jobNumber}
          </div>
        </div>

        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-600 text-xl px-2"
        >
          ×
        </button>
      </div>

      {/* BODY (scrollable) */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-24 space-y-4 text-sm text-gray-700">
        {/* SUMMARY */}
        <section>

          <div className="space-y-1">
            <div className="truncate">
              <span className="text-gray-500">SKU:</span>{" "}
              <span className="text-gray-900">{job.name || "N/A"}</span>
            </div>
            <div>
              <span className="text-gray-500">Quantity:</span>{" "}
              <span className="text-gray-900">{job.qty ?? "N/A"}</span>
            </div>
            <div>
              <span className="text-gray-500">Status:</span>{" "}
              <span className="text-gray-900">{job.status || "N/A"}</span>
            </div>
            <div>
              <span className="text-gray-500">Due Date:</span>{" "}
              <span className="text-gray-900">
                {job.dueDate ? job.dueDate.split("T")[0] : "N/A"}
              </span>
            </div>
            {job.productCode && (
              <div>
                <span className="text-gray-500">Product Code:</span>{" "}
                <span className="text-gray-900">{job.productCode}</span>
              </div>
            )}
            {operationLabel && (
              <div>
                <span className="text-gray-500">Operation:</span>{" "}
                <span className="text-purple-600 font-semibold">
                  {operationLabel}
                </span>
              </div>
            )}
          </div>
        </section>

        {/* ROUTING */}
        <section>
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
            Routing Steps
          </div>

          {routingLoading && (
            <div className="text-xs text-gray-500">Loading routing…</div>
          )}

          {routingError && (
            <div className="text-xs text-red-600">{routingError}</div>
          )}

          {!routingLoading && !routingError && routing.length === 0 && (
            <div className="text-xs text-gray-500">No routing found.</div>
          )}

          {routing.length > 0 && !routingLoading && !routingError && (
            <div className="space-y-1">
              {[...routing]
                .sort((a, b) => (a.stepNumber || 0) - (b.stepNumber || 0))
                .map((step, i) => {
                  const stepNumber = step.stepNumber ?? "?";
                  const workCenter = step.workCenter || "N/A";
                  const operationCode = step.operationCode || "N/A";
                  const status = step.status || "Unknown";
                  const dueDate = step.estimatedEndDate
                    ? step.estimatedEndDate.split("T")[0]
                    : "N/A";

                  const isClicked =
                    selectedRoutingStep?.stepNumber === step.stepNumber;

                  // Active session indicator for this routing step
                  const isActiveStep =
                    jobSession &&
                    (jobSession.state === "running" ||
                      jobSession.state === "paused") &&
                    jobSession.operationCode === step.operationCode;

                  let rowClass = "bg-slate-200 hover:bg-slate-100";
                  if (isClicked)
                    rowClass =
                      "bg-purple-50 border-l-2 border-purple-500";

                  return (
                    <div
                      key={i}
                      onClick={() =>
                        onRoutingStepSelect && onRoutingStepSelect(step)
                      }
                      className={`
                        px-2 py-2 rounded cursor-pointer transition
                        ${rowClass}
                      `}
                    >
                      {/* FIRST LINE — Step / WC / Operation + Active indicator */}
                      <div className="flex items-center gap-2 text-xs font-medium text-gray-900">
                        <div className="flex items-center gap-2 min-w-0">
                          <span>{stepNumber}</span>
                          <span className="opacity-40">•</span>
                          <span>{workCenter}</span>
                          <span className="opacity-40">•</span>
                          <span className="truncate">{operationCode}</span>
                        </div>
                        {isActiveStep && (
                          <span className="ml-auto text-[10px] text-green-600 flex-shrink-0">
                            ● Active
                          </span>
                        )}
                      </div>

                      {/* SECOND LINE — Status + Due Date */}
                      <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-1">
                        <span>{status}</span>
                        <span className="opacity-40">•</span>
                        <span>{dueDate}</span>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </section>
      </div>

      {/* BOTTOM ACTION BAR */}
      <div
        className="
          border-t border-gray-200
          bg-slate-200
          px-4 py-3
          flex items-center gap-3
        "
      >
        {/* Left side: Job + Operation */}
        <div className="flex flex-col text-xs text-gray-500">
          <span className="text-gray-900 font-semibold">
            {job.jobNumber}
          </span>
          {operationLabel && (
            <span className="text-purple-600">
              {operationLabel}
            </span>
          )}
        </div>

        {/* Right side: Time control buttons */}
        <div className="ml-auto flex items-center gap-2">
          {/* No active session → START */}
          {!hasSession && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!canStart) {
                  alert(
                    "Please select a routing step or valid operation before starting this job."
                  );
                  return;
                }
                onStartJob && onStartJob(job);
              }}
              disabled={!canStart}
              className={`
                text-gray-900 text-xs px-3 py-1 rounded
                ${
                  canStart
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-gray-600 cursor-not-allowed opacity-60"
                }
              `}
            >
              START
            </button>
          )}

          {/* Running → PAUSE + COMPLETE */}
          {isRunning && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPauseJob && onPauseJob(job);
                }}
                className="
                  bg-yellow-500 hover:bg-yellow-600
                  text-gray-900 text-[11px] px-2 py-1 rounded
                "
              >
                Pause
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCompleteJob && onCompleteJob(job);
                }}
                className="
                  bg-red-600 hover:bg-red-700
                  text-gray-900 text-[11px] px-2 py-1 rounded
                "
              >
                Complete
              </button>
            </>
          )}

          {/* Paused → RESUME + COMPLETE */}
          {isPaused && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onResumeJob && onResumeJob(job);
                }}
                className="
                  bg-green-600 hover:bg-green-700
                  text-gray-900 text-[11px] px-2 py-1 rounded
                "
              >
                Resume
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCompleteJob && onCompleteJob(job);
                }}
                className="
                  bg-red-600 hover:bg-red-700
                  text-gray-900 text-[11px] px-2 py-1 rounded
                "
              >
                Complete
              </button>
            </>
          )}
        </div>
      </div>
    </aside>
  );
}
